import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Hook to handle timeout.
 * @param delay in ms
 * @param callback
 * @returns [restart, clear, isWaiting]
 */
export function useTimeout(delay = 0, callback?: () => void) {
  const [isWaiting, setIsWaiting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef<() => void>(callback || (() => {}));

  const restart = useCallback(() => {
    setIsWaiting(true);
    timeoutRef.current && clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      setIsWaiting(false);
      callbackRef.current();
    }, delay);
  }, [delay]);

  const clear = useCallback(() => {
    setIsWaiting(false);
    timeoutRef.current && clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (callback) {
      callbackRef.current = callback;
    }
  }, [callback]);

  return {restart, clear, isWaiting};
}
